/**
 * order-number.ts — generates human-readable order identifiers.
 *
 * Format: WND-YYMMDD-XXXXXX
 *   - WND     : static brand prefix
 *   - YYMMDD  : UTC date of order creation (2-digit year, zero-padded month/day)
 *   - XXXXXX  : 6 Crockford base32 characters (~10^9 combinations per day)
 *
 * Example: WND-260628-K7F3Q2
 *
 * Alphabet (Crockford base32, 32 chars):
 *   0123456789ABCDEFGHJKMNPQRSTVWXYZ
 *   Excluded: I, L, O, U — anti-ambiguity (look-alike with 1, 1, 0, V).
 *
 * The function is pure (no I/O, injectable clock + rng) so it is fully
 * deterministic in tests. Production callers pass `crypto.getRandomValues`
 * or `crypto.randomBytes` (Node) as the rng.
 *
 * Source: spec 2026-06-25-wandero-local-orders-copy-store §D1.
 */

/** Crockford base32 alphabet (32 chars, no I/L/O/U). */
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

// Verify at module load-time (guard against accidental edits).
if (CROCKFORD_ALPHABET.length !== 32) {
  throw new Error(`Crockford alphabet must be 32 chars, got ${CROCKFORD_ALPHABET.length}`);
}

/**
 * Generate a WND-YYMMDD-XXXXXX order number.
 *
 * @param now  The current date — used for the YYMMDD segment (UTC).
 * @param rng  Random source. Accepts either:
 *             - `() => Uint8Array` (e.g. `() => crypto.getRandomValues(new Uint8Array(6))`)
 *             - `() => number` (e.g. `() => Math.random()`)
 *             When a Uint8Array is returned it must have at least 6 bytes.
 *             When a number is returned it should be in [0, 1).
 *
 * @returns `"WND-YYMMDD-XXXXXX"` — always 18 characters.
 */
export function generateOrderNumber(now: Date, rng: () => Uint8Array | number): string {
  // ── Date segment (UTC) ────────────────────────────────────────────────────
  const yy = String(now.getUTCFullYear()).slice(-2);
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  const datePart = `${yy}${mm}${dd}`;

  // ── Random segment (6 Crockford base32 chars) ─────────────────────────────
  const rngResult = rng();
  let randomPart: string;

  if (rngResult instanceof Uint8Array) {
    // Use bytes directly — each byte modulo 32 selects an alphabet character.
    // Slight modulo bias (~0.4%) is acceptable for order-number collision space.
    randomPart = Array.from(rngResult.slice(0, 6))
      .map((b) => CROCKFORD_ALPHABET[b % 32])
      .join('');
  } else {
    // Fallback: number in [0, 1) → scale to 32^6 and encode digit-by-digit.
    let n = Math.floor(rngResult * 32 ** 6);
    let chars = '';
    for (let i = 0; i < 6; i++) {
      chars = (CROCKFORD_ALPHABET[n % 32] ?? '0') + chars;
      n = Math.floor(n / 32);
    }
    randomPart = chars;
  }

  return `WND-${datePart}-${randomPart}`;
}
